import React from 'react'

const LOCALE = 'en-us'
const CST_TIMEZONE = 'America/Chicago'

export default class UtilService {

    static DEFAULT_THUMBNAIL_URL = '/static-assets/NoImageAvailable_55x44.png'
    static DEFAULT_FULL_IMAGE_URL = '/static-assets/NoImageAvailable_715x572.png'

    static importExportStatues = {
        ToDo: 1,
        InProgress: 2,
        Completed: 3,
        Failed: 4,
        CompletedAndRejected: 5
    }

    static monthAbbreviations = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ]

    static days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

    static isUnauthenticatedRoute(route) {
        if (undefined === route) {
            return true
        }
        const routes = {
            '/': true,
            '/login': true,
            '/login/gsm': true,
            '/login/gsm2': true,
            '/logout': true,
            '/loggedOut': true
        }
        return !!routes[route]
    }

    static getNumeralValue(number, decimalPlace) {
        decimalPlace = (!decimalPlace || decimalPlace < 0) ? 0 : decimalPlace
        const power = (number).toPrecision(2).split('e')

        const floorNumber = power.length === 1 ?
            0 : Math.floor(Math.min(power[1].slice(1), 14) / 3)

        const convertedNumber = floorNumber < 1 ?
            number.toFixed(0 + decimalPlace) :
            (number / Math.pow(10, floorNumber * 3)).toFixed(1 + decimalPlace)

        const abbrevatedNumber = convertedNumber < 0 ?
            convertedNumber : Math.abs(convertedNumber)

        return abbrevatedNumber + ['', ' K', ' M', ' B', ' T'][floorNumber]
    }

    static epochTimeToDate = (epochTime) => {
        return new Date(epochTime * 1000).toLocaleString()
    }

    static formatTimeAMPM = (date) => {
        let hours = date.getHours()
        let minutes = date.getMinutes()
        let seconds = date.getSeconds()
        const ampm = hours >= 12 ? 'PM' : 'AM'
        hours = hours % 12
        hours = hours ? hours : 12 // the hour '0' should be '12'
        hours = hours < 10 ? '0' + hours : hours
        minutes = minutes < 10 ? '0' + minutes : minutes
        seconds = seconds < 10 ? '0' + seconds : seconds
        return hours + '-' + minutes + '-' + seconds + ' ' + ampm
    }

    static getCurrentDateTime = () => {
        const cstTime = new Date().toLocaleString(LOCALE, { timeZone: CST_TIMEZONE })
        const date = new Date(cstTime)
        let d = date.getDate()
        let m = date.getMonth() + 1
        const y = date.getFullYear()
        if (d < 10) {
            d = `0${d}`
        }
        if (m < 10) {
            m = `0${m}`
        }
        return m + '-' + d + '-' + y + '-' + UtilService.formatTimeAMPM(date)
    }

    static getClientLabel(client) {

        switch (true) {

            case !client.name && !client.code:
                return null

            case !client.name:
                return `${client.code}`

            case !client.code:
                return client.name

            default:
                return `${client.name} (${client.code})`

        }

    }

    static safeLog(message) {
        /* eslint-disable no-console */
        console.log(message)
        /* eslint-enable no-console */
    }

    static skipDeepLinkingRoute(route) {
        if (this.isUnauthenticatedRoute(route)) {
            return true
        }
        const routes = {
        }
        return !!routes[route]
    }

    static chunkString(sourceString, chunkSize, fromRight) {

        const source = 'string' === typeof sourceString
            ? sourceString
            : ''

        const count = Math.ceil(source.length / chunkSize)
        const chunks = []
        let offset = 0
        let size = chunkSize
        if (fromRight) {
            size = source.length % chunkSize
            if (!size) {
                size = chunkSize
            }
        }

        for (let i = 0; i < count; ++i) {
            chunks.push(source.substr(offset, size))
            offset += size
            if (fromRight && !i) {
                size = chunkSize
            }
        }

        return chunks

    }

    static formatDecimalString(decimal) {
        const a = decimal.toString().split('.')
        a[0] = this.chunkString(a[0], 3, true).join(',')
        return a.join('.')
    }

    static formatRoundedThousandsString(decimal) {
        const a = decimal.toString().split('.')

        if (1000 > decimal) return a[0]

        const roundedNumber = Math.round(parseInt(a[0]) / 1000)
        const formatedNumber = this.chunkString(roundedNumber.toString(), 3, true).join(',')
        return `${formatedNumber}K`
    }

    static formatDateForMonthAndYearTickLabel(timestamp) {
        const d = new Date(timestamp)
        const a = d.toUTCString().split(' ')
        return 0 === d.getUTCMonth()
            ? `${a[2]}\n${a[3]}`
            : a[2]
    }

    static formatDateForMonthAndYearLabel(timestamp) {
        const d = new Date(timestamp)
        const a = d.toUTCString().split(' ')
        return [
            a[2],
            a[3]
        ].join(' ')
    }

    static formatNumericDateForInput(date) {
        if (date instanceof Date) {
            const yyyy = date.getFullYear()
            const mm = UtilService.padLeft(date.getMonth() + 1, 2)
            const dd = UtilService.padLeft(date.getDate(), 2)
            return `${mm}/${dd}/${yyyy}`
        }

        return ''
    }

    static parseNumericDateFromInput(date) {
        if (typeof date === 'string') {
            const matches = date.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/)
            if (matches) {
                const month = parseInt(matches[1], 10)
                const day = parseInt(matches[2], 10)
                const year = parseInt(matches[3], 10)

                if (month > 0 && month <= 12 && day <= 31) {
                    return new Date(year, month - 1, day)
                }
            }
        }

        return null
    }

    static formatTextDateForInput(date) {
        if (date instanceof Date) {
            const yyyy = date.getFullYear()
            const mmm = UtilService.monthAbbreviations[date.getMonth()]
            const dd = UtilService.padLeft(date.getDate(), 2)
            return `${mmm} ${dd}, ${yyyy}`
        }

        return ''
    }

    static parseTextDateFromInput(date) {
        if (typeof date === 'string') {
            const matches = date.match(/^\s*([a-zA-Z]{3})\s+(\d{1,2})\,?\s+(\d{4})\s*$/)
            if (matches) {
                const monthStr = matches[1].substr(0, 1).toUpperCase() + matches[1].substr(1, 2).toLowerCase()
                const month = UtilService.monthAbbreviations.indexOf(monthStr)
                const day = parseInt(matches[2], 10)
                const year = parseInt(matches[3], 10)

                if (month !== -1 && day <= 31) {
                    return new Date(year, month, day)
                }
            }
        }

        return null
    }

    static padLeft(str, count, char = '0') {
        str = str.toString()
        return Array(count - String(str).length + 1).join(char) + str
    }

    static generateQueryString(obj) {
        return '?'.concat(this.encodeQueryObject(obj))
    }

    static encodeQueryObject(obj) {
        if (!Object.keys(obj).length) {
            return ''
        }

        const encodedComponents = []
        Object.entries(obj).forEach((kvp) => {
            encodedComponents.push(`${encodeURIComponent(kvp[0])}=${encodeURIComponent(kvp[1].toString())}`)
        })

        return encodedComponents.join('&')
    }

    static getRouteParts(route) {
        return route.split('/').filter(p => p)
    }

    static regexEscape(string) {
        return (typeof string === 'string') ? string.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') : ''
    }

    static renderHighlightedSearchTerms(string, query) {
        if (!query) {
            return string
        }

        const regex = new RegExp(`(${UtilService.regexEscape(query)})`, 'gi')
        const parts = string.split(regex)

        return parts
            .filter(part => !!part)
            .map((part, index) => {
                return part.toLowerCase() === query.toLowerCase() ?
                    <strong key={index}>{part}</strong> :
                    <span key={index}>{part}</span>
            }
            )
    }

    static splitComponentProps(props, Component) {
        const componentPropTypes = Component.propTypes

        const parentProps = {}
        const childProps = {}

        Object.entries(props).forEach(([propName, propValue]) => {
            if (componentPropTypes[propName]) {
                parentProps[propName] = propValue
            }
            else {
                childProps[propName] = propValue
            }
        })

        return [parentProps, childProps]
    }

    static keyValueResolver(keyValuePairs, key) {
        if (!key) {
            return key
        }

        const item = keyValuePairs.find(i => i.key === key)

        if (!item) {
            return key
        }

        return item.value
    }

    static extractAccountOrn(orn) {
        const segments = orn ? orn.split(':') : []

        if (segments.length >= 3 && segments[0] === 'orn' && segments[1] === 'entity') {
            return segments.slice(0, 3).join(':')
        }

        return null
    }

    static getUniqueValuesFromArray(arr) {
        return arr.filter((value, index, self) => {
            return value && (self.indexOf(value) === index)
        })
    }

    static getFileExtension(fileName) {
        const matches = fileName.match(/\.[^\.]+$/)
        return matches && matches.length ? matches[0].substr(1) : null
    }

    static getCurrentDate() {
        const now = new Date()
        now.setHours(0, 0, 0, 0)
        return now
    }

    static getDateFromEpochWithoutTime(epochDate) {
        const now = new Date(epochDate * 1000)
        now.setHours(0, 0, 0, 0)
        return now
    }

    static getDateFromEpoch(epochDate) {
        return new Date(epochDate * 1000)
    }

    static dateTimeConversion(dataDate, dataTime) {
        const data = dataTime.split(':')
        if (data[0] < 10) {
            data[0] = data[0].padStart(2, '0')
        }

        const value = data.join(':')
        const time = value.match(/\d{2}/g)

        if (time[0] === '12') {
            time[0] = '00'
        }
        if (value.indexOf('pm') > -1) {
            time[0] = parseInt(time[0]) + 12
        }

        dataTime = time.join(':')

        const d = new Date(dataDate * 1000)
        const timeParts = dataTime.split(':')

        d.setHours(timeParts[0])
        d.setMinutes(timeParts[1])

        return d.getTime() / 1000
    }

    static ucaseFirstLetter(str) {
        return str.charAt(0).toUpperCase() + str.slice(1)
    }

    static formatInteger(number) {
        return Intl.NumberFormat(LOCALE).format(number)
    }

    static formatDecimal(number, min = 2, max = 2) {
        return Intl.NumberFormat(LOCALE, { minimumFractionDigits: min, maximumFractionDigits: max }).format(number)
    }

    static formatDateRange(from, to) {
        return `${UtilService.formatTextDateForInput(new Date(from * 1000))} - ${UtilService.formatTextDateForInput(new Date(to * 1000))}`
    }

    static formatPhoneNumber(phone) {
        return `${phone.substr(0, 3)}-${phone.substr(3, 3)}-${phone.substr(6)}`
    }

    static formatMilliSecondsToDate(milliseconds) {
        return (new Date(milliseconds)).toLocaleDateString(LOCALE, { year: '2-digit', month: '2-digit', day: '2-digit' })
    }

}


// WEBPACK FOOTER //
// src/services/util/index.js